/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Grid } from '@mui/material';
import columns from './table_config';
import PropTypes from 'prop-types';
import Datagrid from '../../../ui/Datagrid';
import SelectDrillDetails from '../../../ui/SelectDrillDetails';
import { useDispatch } from 'react-redux';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import { REDUX_ACTIONS } from '../../../../shared/config/constants';
import { isMobile } from 'react-device-detect';
import MobileRolloverRunInput from '../../../mobile/hydraulics/featuredWellRunInputs/rolloverRunInputs';
import MobileSelectDrillDetails from '../../../mobile/hydraulics/SelectDrillDetails';
import MobileSelectEditInputTextField from '../../../mobile/hydraulics/SelectEditInputTextField';
import SelectEditInputTextField from '../../../ui/SelectEditInputTextField';

const RolloverRunInputs = ({
  rolloverData,
  wellboreData,
  setRolloverData,
  hydraulicsLanding,
  mudPumpData
}) => {
  const apiRef = useGridApiRef();
  const [properties, setProperties] = useState([]);
  const dispatch = useDispatch();

  function generateRandom() {
    const length = 24;
    const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let retVal = '';
    for (let i = 0, n = charset.length; i < length; ++i) {
      retVal += charset.charAt(Math.floor(Math.random() * n));
    }
    return retVal;
  }

  useEffect(() => {
    const formatFluid = (fluid) =>
    `${fluid.name.trim()} @ ${fluid.MD.toFixed(
      hydraulicsLanding?.units?.drillFluids?.MD.decimals
    )}${hydraulicsLanding?.units?.drillFluids?.MD.label}`;

    const drillFluids = hydraulicsLanding?.data?.drillFluids || [];
    const drillFluidTo = drillFluids.find(
      (item) => item.fluid_id === rolloverData.toFluidId
    );
    const defaultFluid = drillFluids?.[0];

    const mudpumpsData = mudPumpData || [];
    const hasMudpumps = mudpumpsData.length > 0;

    const pumps_output = hasMudpumps
      ? Number(mudpumpsData[0]?.bbl_stk || 0)
      : '';

    const pumps_spm = hasMudpumps
      ? mudpumpsData.reduce((sum, pump) => {
        const spm = Number(pump?.spm ?? 0);
        return sum + (Number.isFinite(spm) ? spm : 0);
      }, 0)
      : '';

    const modifiedRolloverData = {
      fromFluidId: wellboreData?.drillFluidsId
        ? wellboreData?.drillFluidsId
        : formatFluid(defaultFluid),
      toFluidId: drillFluidTo ? formatFluid(drillFluidTo) : formatFluid(defaultFluid),
    };

    modifiedRolloverData.pumps_output = rolloverData?.pumps_output ? rolloverData?.pumps_output : pumps_output;
    modifiedRolloverData.pumps_spm = rolloverData?.pumps_spm ? rolloverData?.pumps_spm : pumps_spm;

    setProperties((prev) => {
      if (prev?.length && prev.some((row) => row.isDirty)) {
        return prev;
      }
      const existingId = prev?.[0]?._id || generateRandom();
      return [{ _id: existingId, ...modifiedRolloverData }];
    });
  }, [rolloverData, wellboreData, hydraulicsLanding, mudPumpData]);

  const formatRolloverData = (item, drillFluids, units) => {
    const getFluidId = (fluidLabel) => {
      const match = drillFluids?.find(
        (fluid) =>
          `${fluid?.name?.trim()} @ ${fluid?.MD.toFixed(units?.MD.decimals)}${units?.MD.label}` ===
          fluidLabel
      );
      return match?.fluid_id || null;
    };

    return {
      _id: item._id,
      fromFluidId: getFluidId(wellboreData?.drillFluidsId),
      toFluidId: getFluidId(item?.toFluidId),
      pumps_output: item?.pumps_output,
      pumps_spm: item?.pumps_spm,
      isDirty: item?.isDirty
    };
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      let isValidUpdate = true;

      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          if (
            (field === 'toFluidId' && item.fromFluidId === value) ||
            (field === 'fromFluidId' && item.toFluidId === value)
          ) {
            dispatch(
              enqueueSnackbar(
                'From fluid and To fluid cannot be the same',
                'error',
                new Date().getTime() + Math.random()
              )
            );
            isValidUpdate = false;
            return item;
          }

          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: value,
            isDirty,
          };
        }
        return item;
      });

      if (isValidUpdate) {
        dispatch({
          type: REDUX_ACTIONS.HYDRAULICS_LANDING_RUN_INPUTS_CHANGED,
        });
        setRolloverData(
          formatRolloverData(
            updatedProperties[0],
            hydraulicsLanding?.data?.drillFluids,
            hydraulicsLanding?.units?.drillFluids
          )
        );
      }

      return updatedProperties;
    });
  };

  const renderDrillFluidCell = (params) => {
    if (isMobile) {
      return (
            <MobileSelectDrillDetails
              {...params}
              closeCellAfterSelect={true}
              updateData={updateData}
              data={hydraulicsLanding?.data?.drillFluids}
              label={'ft'}
              decimals={2}
              loading={hydraulicsLanding?.hydraulicsRunLoading}
            />
      );
    }
    return (
      <SelectDrillDetails
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={hydraulicsLanding?.data?.drillFluids}
        label={'ft'}
        decimals={2}
      />
    );
  };

  const renderValueCell = (params) => {
    if (isMobile) {
      return (
          <MobileSelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />
      );
    }
    return (
        <SelectEditInputTextField
          numberFormatOnly
          {...params}
          updateData={updateData}
        />
    );
  };

  return (
    <>
      <Grid container width='100%'>
        <Grid item xs={12}>
          {
            isMobile
              ? (
                <MobileRolloverRunInput
                data={properties}
                columns={columns(renderValueCell, renderDrillFluidCell)}
                updateData={updateData}
                loading={hydraulicsLanding?.hydraulicsRunLoading}
                />
                )
              : (
                <Datagrid
                  apiRef={apiRef}
                  data={properties}
                  loading={hydraulicsLanding?.hydraulicsRunLoading}
                  sx={{
                    marginLeft: '-6px',
                    '.MuiDataGrid-cell': {
                      fontSize: '1rem',
                      fontWeight: 900,
                      pointerEvents: hydraulicsLanding?.hydraulicsRunLoading ? 'none' : 'auto',
                      backgroundColor: hydraulicsLanding?.hydraulicsRunLoading ? '#f5f5f5' : 'inherit'
                    },
                    '.MuiDataGrid-columnHeader': {
                      fontSize: '1.2rem',
                      fontWeight: '900 !important'
                    },
                    '& .MuiDataGrid-footerContainer': {
                      display: 'none',
                    },
                  }}
                  columns={columns(renderValueCell, renderDrillFluidCell)}
                  onRowClick={(params, evt) => {
                    evt.preventDefault();
                    evt.stopPropagation();
                  }}
                  disableStickyHeader={false}
                  autoHeight={false}
                  autoheight
                  height={'150px'}
                  pageSize={100}
                  editMode="cell"
                  toolbar={{
                    options: {
                      columns: false,
                      filters: false,
                      density: false,
                      export: false
                    },
                  }}
                />
                )
          }
        </Grid>
      </Grid>
    </>
  );
};
RolloverRunInputs.propTypes = {
  rolloverData: PropTypes.any,
  setRolloverData: PropTypes.func,
  wellboreData: PropTypes.any,
  mudPumpData: PropTypes.any,
  hydraulicsLanding: PropTypes.object
};

export default RolloverRunInputs;
